// SdsRequestForm.js
// Inline Request SDS form: asks 3E to add a product to EWI's catalog (POST /api/sds/requests).
// States: editing -> submitting -> submitted | duplicate | error. Nothing destructive, so no confirmation modal.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TextInput, Button, Stack } from "../ui";
import { createSdsRequest, sdsErrorMessage, sdsRetryAfter } from "../../../api/SdsApi";
import styles from "./SdsLookupSection.module.css";

export const REQUESTS_PATH = "/home/sds_lookup?view=requests";
export const SUCCESS_MESSAGE =
  "We will email you when 3E has it (usually 1-2 business days, up to 30 for manufacturer requests).";

// Mirrors SdsRequestSchema: manufacturer 2..300, material 2..500, product number <= 200
const LIMITS = {
  manufacturerName: { min: 2, max: 300, label: "Manufacturer" },
  materialName: { min: 2, max: 500, label: "Product name" },
  productNumber: { min: 0, max: 200, label: "Product number" },
};

const validateField = (name, value) => {
  const { min, max, label } = LIMITS[name];
  const trimmed = (value || "").trim();
  if (min && !trimmed) return `${label} is required`;
  if (trimmed && trimmed.length < min) return `${label} needs at least ${min} characters`;
  if (trimmed.length > max) return `${label} needs at most ${max} characters`;
  return null;
};

export const validateSdsRequest = (values) => {
  const errors = {};
  Object.keys(LIMITS).forEach((name) => {
    const message = validateField(name, values[name]);
    if (message) errors[name] = message;
  });
  return errors;
};

const formatRequestDate = (iso) => (iso ? String(iso).slice(0, 10) : "an earlier date");

const formatWait = (seconds) =>
  seconds === 1 ? "Try again in 1 second." : `Try again in ${seconds} seconds.`;

/**
 * @param {string} [initialMaterialName]
 * @param {string} [initialManufacturerName]
 * @param {string} [initialProductNumber]
 * @param {string} [delId] - forwarded as del_id for traceability
 * @param {boolean} isOnline - submit is disabled while offline
 * @param {function} [showNotification] - notificationActions.showNotification
 * @param {function} [onSubmitted] - (request) after a successful create
 */
export const SdsRequestForm = ({
  initialMaterialName = "",
  initialManufacturerName = "",
  initialProductNumber = "",
  delId,
  isOnline = true,
  showNotification,
  onSubmitted,
}) => {
  const [values, setValues] = useState({
    manufacturerName: initialManufacturerName || "",
    materialName: initialMaterialName || "",
    productNumber: initialProductNumber || "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [state, setState] = useState("editing");
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);

  const submitting = state === "submitting";
  const canSubmit = isOnline && !submitting;

  const handleChange = (name) => (event) => {
    const value = event.target.value;
    setValues((current) => ({ ...current, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((current) => ({ ...current, [name]: null }));
  };

  const submit = async () => {
    if (!canSubmit) return;
    const errors = validateSdsRequest(values);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setState("submitting");
    setErrorMessage(null);
    setRetryAfter(null);
    try {
      const data = await createSdsRequest({
        manufacturer_name: values.manufacturerName.trim(),
        material_name: values.materialName.trim(),
        product_number: values.productNumber.trim() || null,
        del_id: delId || null,
      });
      const request = data?.request || null;
      setResult(request);
      if (data?.duplicate) {
        setState("duplicate");
        return;
      }
      setState("submitted");
      showNotification?.({ type: "success", title: "SDS requested", message: SUCCESS_MESSAGE });
      onSubmitted?.(request);
    } catch (err) {
      setErrorMessage(sdsErrorMessage(err));
      setRetryAfter(sdsRetryAfter(err));
      setState("error");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submit();
  };

  if (state === "submitted") {
    return (
      <div className={styles.requestOutcome} role="status" data-testid="sds-request-submitted">
        <div className={styles.requestOutcomeTitle}>SDS requested</div>
        <div>
          Request {result?.identifier || "submitted"}. {SUCCESS_MESSAGE}
        </div>
        <Link className={styles.textLink} to={REQUESTS_PATH}>
          See requests
        </Link>
      </div>
    );
  }

  if (state === "duplicate") {
    return (
      <div className={styles.requestOutcome} role="status" data-testid="sds-request-duplicate">
        <div className={styles.requestOutcomeTitle}>Already requested</div>
        <div>
          Already requested on {formatRequestDate(result?.created_at)} by{" "}
          {result?.requested_by_name || "another user"}. 3E is still working on it.
        </div>
        <Link className={styles.textLink} to={REQUESTS_PATH}>
          See requests
        </Link>
      </div>
    );
  }

  return (
    <form
      className={styles.requestForm}
      onSubmit={handleSubmit}
      noValidate
      data-testid="sds-request-form"
    >
      <div className={styles.requestFormTitle}>Request SDS</div>
      <div className={styles.requestFormHint}>
        Ask 3E to add this product to EWI&apos;s catalog. Use the names printed on the container.
      </div>
      <Stack>
        <TextInput
          label="Manufacturer"
          id="sds-request-manufacturer"
          value={values.manufacturerName}
          onChange={handleChange("manufacturerName")}
          disabled={submitting}
          error={fieldErrors.manufacturerName}
        />
        <TextInput
          label="Product name"
          id="sds-request-material"
          value={values.materialName}
          onChange={handleChange("materialName")}
          disabled={submitting}
          error={fieldErrors.materialName}
        />
        <TextInput
          label="Product number (optional)"
          id="sds-request-product-number"
          value={values.productNumber}
          onChange={handleChange("productNumber")}
          disabled={submitting}
          error={fieldErrors.productNumber}
        />
      </Stack>

      {state === "error" && (
        <div className={styles.requestError} role="alert">
          <span>{errorMessage}</span>
          {retryAfter && <span className={styles.waitText}>{formatWait(retryAfter)}</span>}
        </div>
      )}
      {!isOnline && (
        <div className={styles.requestFormHint}>Requesting an SDS needs a connection.</div>
      )}

      <div className={styles.requestFormActions}>
        <Button variant="form" type="submit" disabled={!canSubmit}>
          {submitting ? "Sending..." : state === "error" ? "Retry" : "Request SDS"}
        </Button>
      </div>
    </form>
  );
};
